import React, { useLayoutEffect, useRef, useState } from "react";
import { absMediaUrl } from "../utils/mediaUrl";
import "./SetlistSealedArt.css";
import { t } from "../i18n";

// The unopened gift (founder design C1, locked): a paid setlist has no
// artwork yet BY DESIGN — nothing has been uploaded. So the pending art IS
// the promise: a Request-App record, sealed with a green ribbon carrying the
// DJ's face and name, until the upload "opens" it (the delivered mosaic then
// takes this square over). Center label = the QR-code R on white.
//
// Long names: the name gets whatever ribbon space is left after the photo,
// then cuts with an ellipsis — the photo always survives.
//
// SCALE-PROOF (founder bug): the internals are drawn at a fixed
// 148px and the whole square scales to whatever box it's mounted in. The
// old version let the container resize the box while every inset stayed in
// 148-px terms — at the pending page's 80px mobile square the ribbon
// swallowed the art. One ResizeObserver here fixes every surface at once
// (checkout 148 / DJ preview 132 / pending 140→80 / counter 120) and any
// future one for free.

const BASE = 148;

const initials = (name?: string | null) =>
  (name || "DJ")
    .split(/\s+/)
    .map((w) => Array.from(w)[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

const SetlistSealedArt: React.FC<{
  djName?: string | null;
  djAvatarUrl?: string | null;
  /** Spotify + Apple Music chips riding the bottom-right corner — the same
      pair the checkout art wears. Callers that already overlay their own
      marks (RequestPage's art frame) leave this off. */
  withMarks?: boolean;
  /** Tiny use (Balance's playlist-sale card): no name ribbon — at
      ~64px it would swallow the disc — and the two chips scaled up so they
      still read. Everything else is the same art. */
  compact?: boolean;
}> = ({ djName, djAvatarUrl, withMarks, compact }) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<number | null>(null);
  // A photo that 404s must fall back to initials — never the browser's
  // broken-image glyph on the ribbon (the blue "?", founder decision).
  const [broken, setBroken] = useState(false);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / BASE);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const avatarSrc = broken ? null : absMediaUrl(djAvatarUrl);

  return (
    <div className="ssa-fit" ref={boxRef} aria-label={t("setlistSealedArt.sealedPlaylistWaitingFor")}>
      <div
        className={compact ? "ssa ssa--compact" : "ssa"}
        // Invisible for the single frame before the first measurement so a
        // full-size 148px square never flashes inside a smaller box.
        style={scale === null ? { visibility: "hidden" } : { transform: `scale(${scale})` }}
      >
        <div className="ssa-disc" />
        <div className="ssa-label">
          <img src="/r.png" alt="" />
        </div>
        {!compact && (
        <div className="ssa-ribbon">
          {avatarSrc ? (
            <img
              className="ssa-ava"
              src={avatarSrc}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => setBroken(true)}
            />
          ) : (
            <span className="ssa-ava ssa-ava--initials">{initials(djName)}</span>
          )}
          {djName && <span className="ssa-name">{djName}</span>}
        </div>
        )}
        {withMarks && (
          <span className="ssa-marks" aria-hidden="true">
            <span className="ssa-mark ssa-mark--spotify">
              <svg viewBox="0 0 24 24" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"
                />
              </svg>
            </span>
            <span className="ssa-mark ssa-mark--apple">
              <svg viewBox="0 0 24 24" focusable="false">
                <path
                  fill="currentColor"
                  d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                />
              </svg>
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default SetlistSealedArt;
